import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, StatusBadge, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { CsvExport } from "@/components/CsvExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, ArrowsClockwise, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["planning", "in_progress", "on_hold", "completed"];

export default function Projects() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", client_name: "", location: "", status: "planning",
    budget: 0, start_date: "", end_date: "", description: "",
  });

  const load = useCallback(() => api.get("/projects").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/projects", { ...form, budget: Number(form.budget) });
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", client_name: "", location: "", status: "planning", budget: 0, start_date: "", end_date: "", description: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="projects-page">
      <PageHeader
        title="Projects"
        subtitle="All active and archived HVAC projects."
        module="Projects · PS"
        ribbon={
          <>
            <BcRibbonGroup label="New">
              <BcRibbonBtn
                icon={Plus}
                label="New Project"
                variant="primary"
                onClick={() => setOpen(true)}
                testid="ribbon-new-project"
              />
            </BcRibbonGroup>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn
                icon={ArrowsClockwise}
                label="Refresh"
                onClick={load}
                testid="ribbon-refresh"
              />
            </BcRibbonGroup>
            <BcRibbonGroup label="Report">
              <CsvExport resource="projects" filename="projects.csv" testid="export-projects-csv" render={({ onClick }) => (
                <BcRibbonBtn icon={FileArrowDown} label="Export CSV" onClick={onClick} testid="ribbon-export-csv" />
              )} />
            </BcRibbonGroup>
          </>
        }
      />
      <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent className="w-[440px] sm:max-w-md">
              <SheetHeader><SheetTitle className="font-display">Create Project</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-6">
                <Field label="Project name"><Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Client"><Input data-testid="project-client-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></Field>
                <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Budget"><Input type="number" data-testid="project-budget-input" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></Field>
                  <Field label="Status">
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Start date"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
                  <Field label="End date"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
                </div>
                <Field label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                <Button onClick={submit} data-testid="save-project-button" className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">Save Project</Button>
              </div>
            </SheetContent>
          </Sheet>

      <div className="p-6">
        {rows.length === 0 ? (
          <EmptyState title="No projects yet" description="Create your first HVAC project to start tracking budgets, invoices and expenses." />
        ) : (
          <div className="bc-card overflow-hidden" data-testid="projects-table">
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((p) => (
                  <TableRow key={p.id} data-testid={`project-row-${p.id}`}>
                    <TableCell className="font-medium">
                      <Link to={`/projects/${p.id}`} className="text-[#2E75B6] hover:underline" data-testid={`open-project-${p.id}`}>
                        {p.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-[#605E5C]">{p.client_name}</TableCell>
                    <TableCell className="text-[#605E5C]">{p.location || "—"}</TableCell>
                    <TableCell><StatusBadge status={p.status} /></TableCell>
                    <TableCell className="text-right font-mono-num">{fmtMoney(p.budget)}</TableCell>
                    <TableCell className="text-[#605E5C]">{fmtDate(p.start_date)}</TableCell>
                    <TableCell className="text-[#605E5C]">{fmtDate(p.end_date)}</TableCell>
                    <TableCell><button onClick={() => del(p.id)} data-testid={`delete-project-${p.id}`} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>
  );
}
